import React, { useEffect, useState } from 'react';
import SuperadminLayout from '../../components/SuperadminLayout';
import { approveDeviceRegistration, listDeviceRegistrations, rejectDeviceRegistration } from '../../api/superadmin';
import './SuperadminDevicesPage.css';

const statusLabels = {
  pending: 'En attente',
  approved: 'Approuve',
  rejected: 'Rejete',
};

const SuperadminDevicesPage = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [companyQuery, setCompanyQuery] = useState('');
  // Collision de username a l'approbation (2026-10-11) : { id, detail } -> champ
  // pour choisir un autre username et reapprouver ce device.
  const [collision, setCollision] = useState(null);
  const [customUsername, setCustomUsername] = useState('');
  // Username/mot de passe a donner au vendeur quand le username a ete choisi a la main.
  const [approvedInfo, setApprovedInfo] = useState(null);

  const load = (companyName = '') => {
    setLoading(true);
    listDeviceRegistrations(companyName)
      .then(setDevices)
      .catch(() => setError('Impossible de charger les devices.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), []);

  const handleSearch = (e) => {
    e.preventDefault();
    load(companyQuery);
  };

  const handleDecision = async (id, action, username) => {
    if (username !== undefined && !/^\d{4,8}$/.test(username)) {
      setError('Username : 4 a 8 chiffres uniquement.');
      return;
    }
    setBusyId(id);
    setError('');
    setApprovedInfo(null);
    try {
      const updated = action === 'approve'
        ? await approveDeviceRegistration(id, username)
        : await rejectDeviceRegistration(id);
      setDevices((prev) => prev.map((d) => (d.id === id ? updated : d)));
      if (collision?.id === id) setCollision(null);
      if (updated.password) {
        setApprovedInfo({ username: updated.bank_username, password: updated.password });
      }
    } catch (err) {
      // apiRequest met le corps de la reponse dans err.message (JSON {detail:...} ou texte).
      let msg = 'Action impossible sur ce device.';
      let parsed = null;
      try {
        parsed = JSON.parse(err.message);
        if (parsed && parsed.detail) msg = `${msg} — ${parsed.detail}`;
      } catch (_) {
        if (err.message) msg = `${msg} — ${err.message}`;
      }
      if (parsed?.code === 'username_collision') {
        if (collision?.id !== id) setCustomUsername('');
        setCollision({ id, detail: parsed.detail });
      } else {
        setError(msg);
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SuperadminLayout>
      <h2 className="sad-title">Demandes de device (POS)</h2>

      <form onSubmit={handleSearch} className="sad-search-row">
        <input
          className="sad-search-input"
          type="text"
          placeholder="Rechercher par nom de company"
          value={companyQuery}
          onChange={(e) => setCompanyQuery(e.target.value)}
        />
        <button type="submit" className="sad-search-btn" disabled={loading}>
          {loading ? 'Recherche...' : 'Search'}
        </button>
      </form>

      {error && <p className="sad-error">{error}</p>}
      {approvedInfo && (
        <p className="sad-success">
          Device approuve avec le username <strong>{approvedInfo.username}</strong>. Mot de passe du vendeur :{' '}
          <strong>{approvedInfo.password}</strong> — donne-les au vendeur : sur le POS il doit taper ce username
          (different de celui affiche automatiquement).
        </p>
      )}
      {loading ? (
        <p>Chargement...</p>
      ) : (
        <table className="sad-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Company</th>
              <th>Code</th>
              <th>Telephone</th>
              <th>Statut</th>
              <th>Demande</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => (
              <React.Fragment key={device.id}>
              <tr>
                <td>
                  {device.bank_username || device.generated_username}
                  {device.bank_seq != null && <div className="sad-small">Banque N° {device.bank_seq}</div>}
                  {device.bank_username && device.bank_username !== device.generated_username && (
                    <div className="sad-small">auto : {device.generated_username}</div>
                  )}
                  {/* Meme serial ailleurs : POS au serial fabricant partage (2026-10-19). */}
                  {(device.serial_conflicts || []).map((c) => (
                    <div key={c.id} className="sad-serial-warning">
                      Serial déjà utilisé chez {c.company_name} ({statusLabels[c.status] || c.status})
                      {c.same_device === 'different' && ' — autre appareil'}
                      {c.same_device === 'unknown' && ' — même appareil ? inconnu'}
                      {c.same_device === 'yes' && ' — même appareil'}
                    </div>
                  ))}
                </td>
                <td>{device.company_name}</td>
                <td>{device.company_code}</td>
                <td>{device.company_phone || '—'}</td>
                <td>{statusLabels[device.status] || device.status}</td>
                <td>{new Date(device.requested_at).toLocaleString()}</td>
                <td>
                  {device.status === 'pending' ? (
                    <div className="sad-actions">
                      <button
                        className="sad-approve-btn"
                        disabled={busyId === device.id}
                        onClick={() => handleDecision(device.id, 'approve')}
                      >
                        Approuver
                      </button>
                      <button
                        className="sad-reject-btn"
                        disabled={busyId === device.id}
                        onClick={() => handleDecision(device.id, 'reject')}
                      >
                        Rejeter
                      </button>
                    </div>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
              {collision?.id === device.id && device.status === 'pending' && (
                <tr className="sad-collision-row">
                  <td colSpan={7}>
                    <p className="sad-error">{collision.detail}</p>
                    <form
                      className="sad-collision-form"
                      onSubmit={(e) => { e.preventDefault(); handleDecision(device.id, 'approve', customUsername.trim()); }}
                    >
                      <label>
                        Autre username pour ce POS (4 a 8 chiffres) :{' '}
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={8}
                          value={customUsername}
                          onChange={(e) => setCustomUsername(e.target.value.replace(/\D/g, ''))}
                        />
                      </label>
                      <button type="submit" className="sad-approve-btn" disabled={busyId === device.id}>
                        Approuver avec ce username
                      </button>
                      <button type="button" className="sad-reject-btn" onClick={() => setCollision(null)}>
                        Annuler
                      </button>
                    </form>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </SuperadminLayout>
  );
};

export default SuperadminDevicesPage;
